import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useShareOwnLocation, useCrewLocations } from '../hooks/useCrewLocations';
import Map from './Map';
import { CashbookView } from './CashbookView';
import { usePlayerLocation } from '../game-map/PlayerLocationContext';

function parseResponseSafely(text) {
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

async function crewRequest(url, method = 'GET', body = undefined) {
  const response = await fetch(url, {
    method,
    credentials: 'include',
    headers: {
      ...(method !== 'GET' ? { 'Content-Type': 'application/json' } : {}),
      'X-WP-Nonce': window.pfandpiratData.nonce,
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  const text = await response.text();
  const data = parseResponseSafely(text);

  if (!response.ok) {
    const message =
      (data && typeof data === 'object' && (data.message || data.error)) || `HTTP ${response.status}`;
    const error = new Error(message);
    error.status = response.status;
    error.payload = data;
    throw error;
  }

  return data;
}

function parseDate(value) {
  if (!value) return null;
  const normalized = String(value).replace(' ', 'T');
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatCurrency(value) {
  return Number(value || 0).toLocaleString('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function matchesPeriod(date, mode, month, year) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return false;
  }

  const now = new Date();
  const todayKey = now.toISOString().slice(0, 10);
  const dateKey = date.toISOString().slice(0, 10);

  switch (mode) {
    case 'today':
      return todayKey === dateKey;
    case 'month':
      return date.getFullYear() === Number(year) && date.getMonth() === Number(month);
    case 'year':
      return date.getFullYear() === Number(year);
    case 'all':
    default:
      return true;
  }
}

function buildEntryStats(entries) {
  return entries.reduce(
    (acc, entry) => {
      const amount = Number(entry.amount || 0);
      acc.total += amount;
      if (entry.status === 'redeemed') {
        acc.redeemed += amount;
      } else {
        acc.open += amount;
      }
      return acc;
    },
    { open: 0, redeemed: 0, total: 0 }
  );
}

function buildCashbookStats(entries) {
  return entries.reduce(
    (acc, entry) => {
      const amount = Number(entry.amount || 0);
      acc.balance += amount;
      if (amount >= 0) {
        acc.in += amount;
      } else {
        acc.out += Math.abs(amount);
      }
      return acc;
    },
    { balance: 0, in: 0, out: 0 }
  );
}

function formatDateLabel(date, includeTime = false) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return '—';
  }

  if (includeTime) {
    return `${date.toLocaleDateString('de-DE')} · ${date.toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit',
    })}`;
  }

  return date.toLocaleDateString('de-DE');
}

function resolveGoalTarget(periodMode, selectedCrew, crewStatsAll) {
  const rawGoal =
    selectedCrew?.goal_amount ||
    selectedCrew?.monthly_goal ||
    selectedCrew?.target_amount ||
    selectedCrew?.crew_goal ||
    selectedCrew?.meta?.goal_amount ||
    null;

  if (rawGoal && Number(rawGoal) > 0) {
    return Number(rawGoal);
  }

  switch (periodMode) {
    case 'today':
      return 2;
    case 'month':
      return 25;
    case 'year':
      return 120;
    case 'all':
    default:
      return Math.max(1500, Math.ceil(Number(crewStatsAll?.total || 0)));
  }
}

function buildActivityFeed(entries, cashbookEntries, memberMap = {}) {
  const entryActivities = (Array.isArray(entries) ? entries : []).map(entry => ({
    id: `entry-${entry.id}`,
    kind: 'entry',
    title: `${formatCurrency(entry._amount)} gesammelt`,
    subtitle: `von ${entry.display_name || entry.user_name || entry.user || memberMap[Number(entry.user_id || 0)]?.display_name || 'Crew-Mitglied'}`,
    amount: entry._amount,
    status: entry.status === 'redeemed' ? 'eingelöst' : 'offen',
    date: entry._parsedDate,
    dateLabel: formatDateLabel(entry._parsedDate, true),
    detail: entry.description || entry.product_name || entry.reason || 'Pfandeintrag',
  }));

  const cashActivities = (Array.isArray(cashbookEntries) ? cashbookEntries : []).map(entry => ({
    id: `cash-${entry.id}`,
    kind: 'cashbook',
    title: entry.reason || (Number(entry._amount) >= 0 ? 'Einzahlung im Kassenbuch' : 'Entnahme im Kassenbuch'),
    subtitle: `${entry._userLabel || 'Unbekannt'} · ${entry._typeLabel || 'Buchung'}`,
    amount: entry._amount,
    status: Number(entry._amount) >= 0 ? 'verbucht +' : 'verbucht -',
    date: entry._parsedDate,
    dateLabel: formatDateLabel(entry._parsedDate, true),
    detail: Number(entry._amount) >= 0 ? 'Positive Kassenbuch-Buchung' : 'Negative Kassenbuch-Buchung',
  }));

  return [...entryActivities, ...cashActivities]
    .filter(item => item.date)
    .sort((a, b) => b.date - a.date)
    .slice(0, 8);
}

function StatusMessage({ type = 'info', children }) {
  if (!children) return null;
  return <div className={`pfp-crew-status is-${type}`}>{children}</div>;
}

function CrewMetricCard({ label, value, note, tone = 'default' }) {
  return (
    <div className={`pfp-crew-metric-card is-${tone}`}>
      <div className="pfp-crew-metric-card__label">{label}</div>
      <div className="pfp-crew-metric-card__value">{value}</div>
      {note ? <div className="pfp-crew-metric-card__note">{note}</div> : null}
    </div>
  );
}

function ActionButton({ children, variant = 'primary', disabled = false, ...props }) {
  return (
    <button
      {...props}
      disabled={disabled}
      className={`pfp-crew-action-button is-${variant}`}
    >
      {children}
    </button>
  );
}

function CrewOptionCard({ crew, isActive, onSelect, actionSlot }) {
  const handleKeyDown = event => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSelect?.();
    }
  };

  return (
    <div
      className={`pfp-crew-option-card ${isActive ? 'is-active' : ''}`}
      onClick={onSelect}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
    >
      <div className="pfp-crew-option-card__head">
        <div>
          <div className="pfp-crew-option-card__title">{crew.title?.rendered || 'Crew'}</div>
          <div className="pfp-crew-option-card__meta">
            Admin: {crew.owner_name || 'Unbekannt'} · {crew.member_count || 0} Mitglieder
          </div>
        </div>
        <div className="pfp-crew-option-card__badge-row">
          {crew.is_owner ? <span className="pfp-crew-badge is-owner">Admin</span> : null}
          {!crew.is_owner && crew.is_member ? <span className="pfp-crew-badge is-member">Mitglied</span> : null}
          {!crew.is_member && crew.is_pending ? <span className="pfp-crew-badge is-pending">Anfrage offen</span> : null}
        </div>
      </div>
      {actionSlot ? <div className="pfp-crew-option-card__actions">{actionSlot}</div> : null}
    </div>
  );
}

function CrewMemberRow({ member, subtitle, actionSlot }) {
  return (
    <li className="pfp-crew-member-row">
      <span className="pfp-crew-member-row__avatar">
        {member.avatarUrl ? (
          <img src={member.avatarUrl} alt="" />
        ) : (
          <span className="pfp-crew-member-row__avatar-fallback">?</span>
        )}
      </span>
      <div className="pfp-crew-member-row__body">
        <div className="pfp-crew-member-row__name">{member.display_name || 'Unbekannt'}</div>
        {subtitle ? <div className="pfp-crew-member-row__subtitle">{subtitle}</div> : null}
      </div>
      {actionSlot ? <div className="pfp-crew-member-row__actions">{actionSlot}</div> : null}
    </li>
  );
}

export function CrewManager({
  crews = [],
  currentCrew,
  onSelect,
  onCreate,
  onJoin,
  onLeave,
  reload,
  userId,
  entries = [],
  crewMembers = {},
  loadMembers,
}) {
  const navigate = useNavigate();
  const now = new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();

  const [newTitle, setNewTitle] = useState('');
  const [members, setMembers] = useState([]);
  const [pendingMembers, setPendingMembers] = useState([]);
  const [cashbookEntries, setCashbookEntries] = useState([]);
  const [cashbookLoading, setCashbookLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedActivityId, setSelectedActivityId] = useState(null);
  const [periodMode, setPeriodMode] = useState('month');
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const playerLocation = usePlayerLocation();
  const [crewSharingEnabled, setCrewSharingEnabled] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);
  const [actionKey, setActionKey] = useState('');
  const [goalInput, setGoalInput] = useState('');

  const monthOptions = [
    'Januar',
    'Februar',
    'März',
    'April',
    'Mai',
    'Juni',
    'Juli',
    'August',
    'September',
    'Oktober',
    'November',
    'Dezember',
  ];

  const selectedCrew = useMemo(
    () => crews.find(crew => Number(crew.id) === Number(currentCrew)) || null,
    [crews, currentCrew]
  );

  const fallbackMemberIds = useMemo(() => {
    if (Array.isArray(crewMembers)) {
      return crewMembers.map(id => Number(id));
    }
    if (crewMembers && Array.isArray(crewMembers[currentCrew])) {
      return crewMembers[currentCrew].map(id => Number(id));
    }
    return [];
  }, [crewMembers, currentCrew]);

  const isOwner = Boolean(selectedCrew?.is_owner || (selectedCrew && Number(selectedCrew.author) === Number(userId)));
  const isMember = Boolean(selectedCrew?.is_member || fallbackMemberIds.includes(Number(userId)) || isOwner);
  const isPending = Boolean(selectedCrew?.is_pending && !isMember);
  const canViewPrivateData = Boolean(selectedCrew?.can_view_private_data || isMember || isOwner);
  const canManageMembers = Boolean(selectedCrew?.can_manage_members || isOwner);

  useEffect(() => {
    const goalValue =
      selectedCrew?.goal_amount ||
      selectedCrew?.monthly_goal ||
      selectedCrew?.target_amount ||
      selectedCrew?.crew_goal ||
      '';
    setGoalInput(goalValue ? String(goalValue).replace('.', ',') : '');
  }, [
    selectedCrew?.goal_amount,
    selectedCrew?.monthly_goal,
    selectedCrew?.target_amount,
    selectedCrew?.crew_goal,
    currentCrew,
  ]);


  useEffect(() => {
    if ((!currentCrew || !crews.some(crew => Number(crew.id) === Number(currentCrew))) && Array.isArray(crews) && crews.length) {
      const myCrew = crews.find(crew => crew.is_owner || crew.is_member || Number(crew.author) === Number(userId));
      if (myCrew) {
        onSelect?.(myCrew.id);
      }
    }
  }, [currentCrew, crews, onSelect, userId]);

  useEffect(() => {
    if (!currentCrew) {
      setActiveTab('overview');
    }
  }, [currentCrew]);

  useShareOwnLocation(canViewPrivateData ? currentCrew : null, { enabled: crewSharingEnabled, position: playerLocation.position });
  const crewLocations = useCrewLocations(canViewPrivateData ? currentCrew : null);

  const toggleCrewSharing = useCallback(async () => {
    if (crewSharingEnabled) {
      setCrewSharingEnabled(false);
      return;
    }
    if (!playerLocation.position) {
      const activated = await playerLocation.requestConsent();
      if (!activated) {
        setStatusMessage({ type: 'error', text: 'Der Crew-Standort wurde nicht freigegeben.' });
        return;
      }
    }
    setCrewSharingEnabled(true);
  }, [crewSharingEnabled, playerLocation]);

  const localPosition = crewSharingEnabled && playerLocation.position ? {
    user_id: Number(userId || 0), display_name: 'Du bist hier', ...playerLocation.position, is_local_user: true,
  } : null;

  const refreshMembersData = useCallback(async () => {
    if (!currentCrew || !canViewPrivateData) {
      setMembers([]);
      return [];
    }

    try {
      const data = await crewRequest(`/wp-json/pfandpirat/v1/crews/${currentCrew}/members`);
      const normalized = Array.isArray(data) ? data : [];
      setMembers(normalized);
      return normalized;
    } catch {
      setMembers([]);
      return [];
    }
  }, [currentCrew, canViewPrivateData]);

  const refreshPendingMembers = useCallback(async () => {
    if (!currentCrew || !canManageMembers) {
      setPendingMembers([]);
      return [];
    }

    try {
      const data = await crewRequest(`/wp-json/pfandpirat/v1/crews/${currentCrew}/pending-members`);
      const normalized = Array.isArray(data) ? data : [];
      setPendingMembers(normalized);
      return normalized;
    } catch {
      setPendingMembers([]);
      return [];
    }
  }, [currentCrew, canManageMembers]);

  const refreshCashbookData = useCallback(async () => {
    if (!currentCrew || !canViewPrivateData) {
      setCashbookEntries([]);
      return [];
    }

    setCashbookLoading(true);
    try {
      const data = await crewRequest(`/wp-json/pfandpirat/v1/cashbook?crew_id=${currentCrew}`);
      const normalized = Array.isArray(data) ? data : [];
      setCashbookEntries(normalized);
      return normalized;
    } catch {
      setCashbookEntries([]);
      return [];
    } finally {
      setCashbookLoading(false);
    }
  }, [currentCrew, canViewPrivateData]);

  useEffect(() => {
    refreshMembersData();
  }, [refreshMembersData]);

  useEffect(() => {
    refreshPendingMembers();
  }, [refreshPendingMembers]);

  useEffect(() => {
    refreshCashbookData();
  }, [refreshCashbookData]);

  const palette = ['#e86a33', '#0b84d8', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444'];
  const userColors = {};
  members.forEach((member, index) => {
    const memberId = Number(member.ID || member.id);
    userColors[memberId] = palette[index % palette.length];
  });
  if (userId && !userColors[Number(userId)]) {
    userColors[Number(userId)] = '#0b84d8';
  }

  function getAvatarUrl(member) {
    let url =
      (member.avatar_urls && (member.avatar_urls['96'] || member.avatar_urls['48'])) || member.avatar || '';
    if (url && url.startsWith('/')) {
      url = `${window.location.origin}${url}`;
    }
    return url && url !== 'undefined' ? url : null;
  }

  const liveLocations = useMemo(() => {
    if (!canViewPrivateData) {
      return [];
    }

    const maxAge = 5 * 60 * 1000;
    const normalizedRemote = (Array.isArray(crewLocations) ? crewLocations : [])
      .filter(location => location && location.latitude != null && location.longitude != null)
      .filter(location => {
        if (!location.timestamp) return true;
        const timestamp = new Date(String(location.timestamp).replace(' ', 'T')).getTime();
        if (!Number.isFinite(timestamp)) return true;
        return Date.now() - timestamp < maxAge;
      })
      .map(location => ({
        ...location,
        user_id: Number(location.user_id ?? location.userId ?? 0),
        latitude: Number(location.latitude),
        longitude: Number(location.longitude),
      }));

    const merged = [];
    const seen = new Set();

    if (localPosition && Number.isFinite(localPosition.latitude) && Number.isFinite(localPosition.longitude)) {
      merged.push(localPosition);
      if (localPosition.user_id) {
        seen.add(String(localPosition.user_id));
      }
    }

    normalizedRemote.forEach(location => {
      const key = String(location.user_id || `${location.latitude}:${location.longitude}`);
      if (seen.has(key)) return;
      merged.push(location);
      seen.add(key);
    });

    return merged;
  }, [crewLocations, localPosition, canViewPrivateData]);

  const normalizedEntries = useMemo(
    () =>
      (Array.isArray(entries) ? entries : []).map(entry => ({
        ...entry,
        _parsedDate: parseDate(entry.timestamp),
        _amount: Number(entry.amount || 0),
      })),
    [entries]
  );

  const normalizedCashbookEntries = useMemo(
    () =>
      (Array.isArray(cashbookEntries) ? cashbookEntries : []).map(entry => {
        const amount = Number(entry.amount || 0);
        const rawType = String(entry.type || '').toLowerCase();
        const typeLabel = rawType === 'withdrawal' || amount < 0 ? 'Entnahme' : rawType === 'balance' ? 'Ausgleich' : 'Einzahlung';
        return {
          ...entry,
          _parsedDate: parseDate(entry.created_at || entry.date || entry.timestamp),
          _amount: amount,
          _userLabel: entry.display_name || entry.user_name || entry.user || 'Unbekannt',
          _typeLabel: typeLabel,
        };
      }),
    [cashbookEntries]
  );

  const filteredEntries = useMemo(
    () => normalizedEntries.filter(entry => matchesPeriod(entry._parsedDate, periodMode, selectedMonth, selectedYear)),
    [normalizedEntries, periodMode, selectedMonth, selectedYear]
  );

  const filteredCashbookEntries = useMemo(
    () => normalizedCashbookEntries.filter(entry => matchesPeriod(entry._parsedDate, periodMode, selectedMonth, selectedYear)),
    [normalizedCashbookEntries, periodMode, selectedMonth, selectedYear]
  );

  const crewStatsAll = useMemo(() => buildEntryStats(normalizedEntries), [normalizedEntries]);
  const crewStatsPeriod = useMemo(() => buildEntryStats(filteredEntries), [filteredEntries]);
  const cashbookStatsAll = useMemo(() => buildCashbookStats(normalizedCashbookEntries), [normalizedCashbookEntries]);
  const cashbookStatsPeriod = useMemo(() => buildCashbookStats(filteredCashbookEntries), [filteredCashbookEntries]);

  const leaderboard = useMemo(
    () =>
      Object.entries(
        filteredEntries.reduce((map, entry) => {
          const key = Number(entry.user_id || 0);
          map[key] = (map[key] || 0) + Number(entry.amount || 0);
          return map;
        }, {})
      )
        .map(([memberId, sum]) => ({ memberId: Number(memberId), sum: Number(sum || 0) }))
        .sort((a, b) => b.sum - a.sum),
    [filteredEntries]
  );

  const memberMap = useMemo(() => {
    const map = {};
    members.forEach(member => {
      map[Number(member.ID || member.id)] = member;
    });
    return map;
  }, [members]);

  const recentCashbookEntries = useMemo(() => filteredCashbookEntries.slice(0, 5), [filteredCashbookEntries]);

  const currentPeriodLabel =
    periodMode === 'month'
      ? `${monthOptions[selectedMonth]} ${selectedYear}`
      : periodMode === 'year'
        ? `Jahr ${selectedYear}`
        : periodMode === 'today'
          ? 'Heute'
          : 'Gesamt';

  const goalTarget = useMemo(
    () => resolveGoalTarget(periodMode, selectedCrew, crewStatsAll),
    [periodMode, selectedCrew, crewStatsAll]
  );
  const goalProgress = Math.max(0, Math.min(100, goalTarget > 0 ? Math.round((crewStatsPeriod.total / goalTarget) * 100) : 0));

  const topCollector = useMemo(() => {
    if (!leaderboard.length) return null;
    const item = leaderboard[0];
    return {
      ...item,
      member: memberMap[item.memberId] || null,
    };
  }, [leaderboard, memberMap]);

  const latestRedemption = useMemo(
    () =>
      normalizedCashbookEntries
        .filter(entry => entry._amount > 0)
        .sort((a, b) => (b._parsedDate?.getTime?.() || 0) - (a._parsedDate?.getTime?.() || 0))[0] || null,
    [normalizedCashbookEntries]
  );

  const activityFeed = useMemo(
    () => buildActivityFeed(filteredEntries, filteredCashbookEntries, memberMap),
    [filteredEntries, filteredCashbookEntries, memberMap]
  );

  const activityRate = useMemo(() => {
    if (!members.length) return 0;
    const activeIds = new Set(filteredEntries.map(entry => Number(entry.user_id || 0)).filter(Boolean));
    return Math.round((activeIds.size / members.length) * 100);
  }, [filteredEntries, members]);

  const averageContribution = useMemo(() => {
    const activeCount = leaderboard.filter(item => item.sum > 0).length;
    return activeCount ? crewStatsPeriod.total / activeCount : 0;
  }, [crewStatsPeriod.total, leaderboard]);

  const redemptionEntries = useMemo(
    () =>
      filteredCashbookEntries
        .filter(entry => entry._amount > 0)
        .sort((a, b) => (b._parsedDate?.getTime?.() || 0) - (a._parsedDate?.getTime?.() || 0)),
    [filteredCashbookEntries]
  );

  const selectedActivity = useMemo(() => {
    if (!activityFeed.length) return null;
    return activityFeed.find(item => item.id === selectedActivityId) || activityFeed[0];
  }, [activityFeed, selectedActivityId]);

  useEffect(() => {
    if (!activityFeed.length) {
      setSelectedActivityId(null);
      return;
    }
    if (!selectedActivityId || !activityFeed.some(item => item.id === selectedActivityId)) {
      setSelectedActivityId(activityFeed[0].id);
    }
  }, [activityFeed, selectedActivityId]);

  const runCrewAction = async (key, action, successMessage) => {
    setActionKey(key);
    setStatusMessage(null);

    try {
      const result = await action();
      setStatusMessage({ type: 'success', text: result?.message || successMessage });
      return result;
    } catch (error) {
      setStatusMessage({ type: 'error', text: error?.message || 'Aktion fehlgeschlagen.' });
      return null;
    } finally {
      setActionKey('');
    }
  };

  const handleCreate = async () => {
    if (!newTitle.trim()) return;

    await runCrewAction(
      'create',
      async () => {
        const result = await onCreate?.(newTitle.trim());
        setNewTitle('');
        await reload?.();
        return result || { message: 'Crew erfolgreich gegründet.' };
      },
      'Crew erfolgreich gegründet.'
    );
  };

  const handleJoin = async crewId => {
    if (!crewId) return;

    await runCrewAction(
      `join-${crewId}`,
      async () => {
        const result = await onJoin?.(crewId);
        await reload?.();
        await refreshMembersData();
        await refreshPendingMembers();
        return result || { message: 'Beitrittsanfrage gesendet.' };
      },
      'Beitrittsanfrage gesendet.'
    );
  };

  const handleLeave = async () => {
    if (!currentCrew) return;

    await runCrewAction(
      'leave',
      async () => {
        const result = await onLeave?.(currentCrew);
        await reload?.();
        await loadMembers?.(currentCrew);
        setMembers([]);
        setPendingMembers([]);
        setCashbookEntries([]);
        onSelect?.(null);
        return result || { message: 'Crew verlassen.' };
      },
      'Crew verlassen.'
    );
  };

  const handleApprove = async memberId => {
    await runCrewAction(
      `approve-${memberId}`,
      async () => {
        const result = await crewRequest(`/wp-json/pfandpirat/v1/crews/${currentCrew}/approve-member`, 'POST', {
          user_id: memberId,
        });
        await reload?.();
        await loadMembers?.(currentCrew);
        await refreshMembersData();
        await refreshPendingMembers();
        return result;
      },
      'Mitglied freigegeben.'
    );
  };

  const handleReject = async memberId => {
    await runCrewAction(
      `reject-${memberId}`,
      async () => {
        const result = await crewRequest(`/wp-json/pfandpirat/v1/crews/${currentCrew}/reject-member`, 'POST', {
          user_id: memberId,
        });
        await reload?.();
        await refreshPendingMembers();
        return result;
      },
      'Anfrage abgelehnt.'
    );
  };

  const handleRemoveMember = async memberId => {
    await runCrewAction(
      `remove-${memberId}`,
      async () => {
        const result = await crewRequest(`/wp-json/pfandpirat/v1/crews/${currentCrew}/remove-member`, 'POST', {
          user_id: memberId,
        });
        await reload?.();
        await loadMembers?.(currentCrew);
        await refreshMembersData();
        await refreshPendingMembers();
        return result;
      },
      'Mitglied entfernt.'
    );
  };

  const handleSaveGoal = async () => {
    if (!currentCrew || !canManageMembers) return;

    const numericGoal = Number(String(goalInput || '').replace(',', '.'));

    if (!Number.isFinite(numericGoal) || numericGoal <= 0) {
      setStatusMessage({ type: 'error', text: 'Bitte ein Crew-Ziel größer als 0 eingeben.' });
      return;
    }

    await runCrewAction(
      'goal-save',
      async () => {
        const result = await crewRequest(`/wp-json/pfandpirat/v1/crews/${currentCrew}/goal`, 'POST', {
          goal_amount: numericGoal,
        });
        await reload?.();
        return result;
      },
      'Crew-Ziel gespeichert.'
    );
  };

  const publicCrews = crews.filter(crew => !crew.is_owner && !crew.is_member);

  if (!currentCrew) {
    return (
      <div className="pfp-crew-shell">
        <div className="pfp-crew-hero">
          <div>
            <h2 className="pfp-crew-hero__title">🏴‍☠️ Crew-Zentrale</h2>
            <p className="pfp-crew-hero__subtitle">Eigene Crew gründen oder einer bestehenden Crew beitreten.</p>
          </div>
        </div>

        {statusMessage && <StatusMessage type={statusMessage.type}>{statusMessage.text}</StatusMessage>}

        <div className="pfp-crew-grid pfp-crew-grid--two-columns">
          <section className="pfp-crew-panel">
            <div className="pfp-crew-panel__header">
              <h3>Neue Crew gründen</h3>
            </div>
            <div className="pfp-crew-create-form">
              <input
                type="text"
                placeholder="Crew-Name"
                value={newTitle}
                onChange={event => setNewTitle(event.target.value)}
              />
              <ActionButton variant="primary" onClick={handleCreate} disabled={actionKey === 'create'}>
                Crew gründen
              </ActionButton>
            </div>
          </section>

          <section className="pfp-crew-panel">
            <div className="pfp-crew-panel__header">
              <h3>Verfügbare Crews</h3>
            </div>
            <div className="pfp-crew-list-cards">
              {crews.length === 0 ? (
                <div className="pfp-crew-empty">Noch keine Crew vorhanden.</div>
              ) : (
                crews.map(crew => (
                  <CrewOptionCard
                    key={crew.id}
                    crew={crew}
                    isActive={false}
                    onSelect={() => onSelect?.(crew.id)}
                    actionSlot={
                      crew.is_owner ? (
                        <span className="pfp-crew-badge is-owner">Deine Crew</span>
                      ) : crew.is_member ? (
                        <span className="pfp-crew-badge is-member">Mitglied</span>
                      ) : crew.is_pending ? (
                        <span className="pfp-crew-badge is-pending">Anfrage offen</span>
                      ) : (
                        <ActionButton
                          variant="success"
                          onClick={event => {
                            event.stopPropagation();
                            handleJoin(crew.id);
                          }}
                          disabled={actionKey === `join-${crew.id}`}
                        >
                          Beitreten
                        </ActionButton>
                      )
                    }
                  />
                ))
              )}
            </div>
          </section>
        </div>
      </div>
    );
  }

  return (
    <div className="pfp-crew-shell">
      <div className="pfp-crew-hero">
        <div>
          <div className="pfp-crew-hero__eyebrow">Aktive Crew</div>
          <h2 className="pfp-crew-hero__title">{selectedCrew?.title?.rendered || 'Crew'}</h2>
          <p className="pfp-crew-hero__subtitle">
            {isOwner ? 'Du verwaltest diese Crew.' : isMember ? 'Du bist Mitglied dieser Crew.' : 'Wähle eine Crew oder sende eine Beitrittsanfrage.'}
          </p>
        </div>
        <div className="pfp-crew-hero__controls">
          <select
            className="pfp-crew-select"
            value={currentCrew || ''}
            onChange={event => {
              setStatusMessage(null);
              onSelect?.(Number(event.target.value) || null);
            }}
          >
            <option value="">– auswählen –</option>
            {crews.map(crew => {
              const labelSuffix = crew.is_owner
                ? ' · Admin'
                : crew.is_member
                  ? ' · Mitglied'
                  : crew.is_pending
                    ? ' · Anfrage offen'
                    : '';
              return (
                <option key={crew.id} value={crew.id}>
                  {crew.title.rendered}
                  {labelSuffix}
                </option>
              );
            })}
          </select>

          <div className="pfp-crew-hero__actions">
            {isOwner ? <span className="pfp-crew-badge is-owner">Crew-Admin</span> : null}
            {!isOwner && isMember ? (
              <ActionButton variant="danger" onClick={handleLeave} disabled={actionKey === 'leave'}>
                Crew verlassen
              </ActionButton>
            ) : null}
            {!isMember && isPending ? <span className="pfp-crew-badge is-pending">Anfrage gesendet</span> : null}
            {!isMember && !isPending ? (
              <ActionButton variant="success" onClick={() => handleJoin(currentCrew)} disabled={actionKey === `join-${currentCrew}`}>
                Beitrittsanfrage senden
              </ActionButton>
            ) : null}
          </div>
        </div>
      </div>

      {statusMessage && <StatusMessage type={statusMessage.type}>{statusMessage.text}</StatusMessage>}

      {!canViewPrivateData ? (
        <>
          <StatusMessage type="info">
            Crew-interne Daten werden erst nach Freigabe durch den Crew-Admin sichtbar.
          </StatusMessage>
          <div className="pfp-crew-grid pfp-crew-grid--two-columns">
            <section className="pfp-crew-panel">
              <div className="pfp-crew-panel__header">
                <h3>Crew-Info</h3>
              </div>
              <div className="pfp-crew-public-list">
                <div><strong>Name:</strong> {selectedCrew?.title?.rendered || 'Crew'}</div>
                <div><strong>Admin:</strong> {selectedCrew?.owner_name || 'Unbekannt'}</div>
                <div><strong>Mitglieder:</strong> {selectedCrew?.member_count || 0}</div>
                <div><strong>Freigabe:</strong> erforderlich</div>
              </div>
            </section>
            <section className="pfp-crew-panel">
              <div className="pfp-crew-panel__header">
                <h3>Weitere Crews</h3>
              </div>
              <div className="pfp-crew-list-cards">
                {publicCrews.length === 0 ? (
                  <div className="pfp-crew-empty">Keine weiteren Crews verfügbar.</div>
                ) : (
                  publicCrews.map(crew => (
                    <CrewOptionCard
                      key={crew.id}
                      crew={crew}
                      isActive={Number(currentCrew) === Number(crew.id)}
                      onSelect={() => onSelect?.(crew.id)}
                    />
                  ))
                )}
              </div>
            </section>
          </div>
        </>
      ) : (
        <>
          <div className="pfp-crew-toolbar">
            <div className="pfp-crew-segmented">
              <button type="button" className={periodMode === 'today' ? 'is-active' : ''} onClick={() => setPeriodMode('today')}>
                Heute
              </button>
              <button type="button" className={periodMode === 'month' ? 'is-active' : ''} onClick={() => setPeriodMode('month')}>
                Monat
              </button>
              <button type="button" className={periodMode === 'year' ? 'is-active' : ''} onClick={() => setPeriodMode('year')}>
                Jahr
              </button>
              <button type="button" className={periodMode === 'all' ? 'is-active' : ''} onClick={() => setPeriodMode('all')}>
                Gesamt
              </button>
            </div>
            <div className="pfp-crew-toolbar__selects">
              <select
                value={selectedMonth}
                onChange={event => {
                  setSelectedMonth(Number(event.target.value));
                  setPeriodMode('month');
                }}
              >
                {monthOptions.map((label, index) => (
                  <option key={label} value={index}>
                    {label}
                  </option>
                ))}
              </select>
              <select
                value={selectedYear}
                onChange={event => {
                  setSelectedYear(Number(event.target.value));
                  setPeriodMode('year');
                }}
              >
                {Array.from(new Set([currentYear, ...normalizedEntries.map(entry => entry._parsedDate?.getFullYear()).filter(Boolean)])).sort((a, b) => b - a).map(year => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
              <span className="pfp-crew-toolbar__label">Ansicht: {currentPeriodLabel}</span>
            </div>
          </div>

          <div className="pfp-crew-metrics-grid">
            <CrewMetricCard
              label="Noch nicht eingelöst"
              value={formatCurrency(crewStatsAll.open)}
              note={`Offener Crew-Schatz · Zeitraum: ${formatCurrency(crewStatsPeriod.open)}`}
              tone="warning"
            />
            <CrewMetricCard
              label="Bereits eingelöst"
              value={formatCurrency(crewStatsAll.redeemed)}
              note={`Im Zeitraum eingelöst: ${formatCurrency(crewStatsPeriod.redeemed)}`}
              tone="success"
            />
            <CrewMetricCard
              label="Crew-Schatz gesamt"
              value={formatCurrency(crewStatsAll.total)}
              note={`Im Zeitraum gesammelt: ${formatCurrency(crewStatsPeriod.total)}`}
              tone="info"
            />
          </div>

          <div className="pfp-crew-metrics-grid">
            <CrewMetricCard
              label="Kassen-Saldo"
              value={formatCurrency(cashbookStatsAll.balance)}
              note={cashbookLoading ? 'Kassenbuch wird geladen …' : 'Aktueller Stand der Crew-Kasse'}
            />
            <CrewMetricCard
              label="Einzahlungen"
              value={formatCurrency(cashbookStatsPeriod.in)}
              note={`Positive Buchungen in ${currentPeriodLabel}`}
              tone="success"
            />
            <CrewMetricCard
              label="Entnahmen"
              value={formatCurrency(cashbookStatsPeriod.out)}
              note={`Negative Buchungen in ${currentPeriodLabel}`}
              tone="danger"
            />
          </div>

          <div className="pfp-crew-tabs">
            <button type="button" className={activeTab === 'overview' ? 'is-active' : ''} onClick={() => setActiveTab('overview')}>
              Übersicht
            </button>
            <button type="button" className={activeTab === 'cashbook' ? 'is-active' : ''} onClick={() => setActiveTab('cashbook')}>
              Kassenbuch
            </button>
            <button type="button" className={activeTab === 'redemptions' ? 'is-active' : ''} onClick={() => setActiveTab('redemptions')}>
              Einlösungen
            </button>
            <button type="button" className={activeTab === 'members' ? 'is-active' : ''} onClick={() => setActiveTab('members')}>
              Mitglieder
            </button>
            <button type="button" className={activeTab === 'map' ? 'is-active' : ''} onClick={() => setActiveTab('map')}>
              Karte
            </button>
          </div>

          {activeTab === 'overview' && (
            <div className="pfp-crew-grid pfp-crew-grid--two-columns">
              <section className="pfp-crew-panel pfp-crew-panel--full-width pfp-crew-goal-panel">
                <div className="pfp-crew-panel__header pfp-crew-panel__header--stack-mobile">
                  <div>
                    <span className="pfp-crew-section-eyebrow">Ziel und Zeitraum</span>
                    <h3>{formatCurrency(crewStatsPeriod.total)} von {formatCurrency(goalTarget)}</h3>
                    <span className="pfp-crew-panel__subtext">Aktive Ansicht: {currentPeriodLabel}</span>
                  </div>
                  <div className="pfp-crew-goal-panel__head-actions">
                    {canManageMembers ? (
                      <div className="pfp-crew-goal-form" onSubmit={event => event.preventDefault()}>
                        <label htmlFor="pfp-crew-goal-input">Crew-Ziel (€)</label>
                        <div className="pfp-crew-goal-form__controls">
                          <input
                            id="pfp-crew-goal-input"
                            type="text"
                            inputMode="decimal"
                            value={goalInput}
                            onChange={event => setGoalInput(event.target.value)}
                            placeholder="z. B. 25,00"
                          />
                          <ActionButton
                            type="button"
                            variant="primary"
                            onClick={handleSaveGoal}
                            disabled={actionKey === 'goal-save'}
                          >
                            Ziel speichern
                          </ActionButton>
                        </div>
                      </div>
                    ) : null}
                    <span className="pfp-crew-progress-badge">{goalProgress}%</span>
                  </div>
                </div>
                <div className="pfp-crew-progress">
                  <span className="pfp-crew-progress__bar" style={{ width: `${goalProgress}%` }} />
                </div>
                <div className="pfp-crew-goal-stats">
                  <div className="pfp-crew-mini-stat">
                    <span>Gesammelt</span>
                    <strong>{formatCurrency(crewStatsPeriod.total)}</strong>
                  </div>
                  <div className="pfp-crew-mini-stat">
                    <span>Eingelöst</span>
                    <strong>{formatCurrency(crewStatsPeriod.redeemed)}</strong>
                  </div>
                  <div className="pfp-crew-mini-stat">
                    <span>Kassen-Saldo</span>
                    <strong>{formatCurrency(cashbookStatsAll.balance)}</strong>
                  </div>
                </div>
              </section>

              <section className="pfp-crew-panel pfp-crew-panel--full-width">
                <div className="pfp-crew-panel__header">
                  <h3>Schnellübersicht</h3>
                </div>
                <div className="pfp-crew-quick-grid">
                  <article className="pfp-crew-highlight-card is-warning">
                    <span className="pfp-crew-highlight-card__eyebrow">Top-Sammler{topCollector && topCollector.sum >= 2 ? 'in' : ''}</span>
                    <strong>{topCollector?.member?.display_name || 'Noch keine Rangliste'}</strong>
                    <span>{topCollector ? `führt aktuell mit ${formatCurrency(topCollector.sum)}` : 'Noch keine Einträge im Zeitraum.'}</span>
                  </article>
                  <article className="pfp-crew-highlight-card is-info">
                    <span className="pfp-crew-highlight-card__eyebrow">Letzte Einlösung</span>
                    <strong>{latestRedemption ? formatCurrency(latestRedemption._amount) : '—'}</strong>
                    <span>
                      {latestRedemption
                        ? `${formatDateLabel(latestRedemption._parsedDate)} · ${latestRedemption.reason || latestRedemption._typeLabel}`
                        : 'Noch keine Einlösung vorhanden.'}
                    </span>
                  </article>
                  <article className="pfp-crew-highlight-card is-success">
                    <span className="pfp-crew-highlight-card__eyebrow">Kassenbuch aktiv</span>
                    <strong>Ein- und Auszahlungen getrennt</strong>
                    <span>voller Erhalt der bisherigen Funktionalität</span>
                  </article>
                </div>
              </section>

              <section className="pfp-crew-panel">
                <div className="pfp-crew-panel__header">
                  <h3>Letzte Crew-Aktivitäten</h3>
                  <button type="button" className="pfp-crew-link-button" onClick={() => setActiveTab('cashbook')}>
                    Alle ansehen
                  </button>
                </div>
                {activityFeed.length === 0 ? (
                  <div className="pfp-crew-empty">Keine Aktivitäten für diesen Zeitraum.</div>
                ) : (
                  <div className="pfp-crew-activity-list">
                    {activityFeed.map(item => (
                      <button
                        type="button"
                        key={item.id}
                        className={`pfp-crew-activity-list__item ${selectedActivity?.id === item.id ? 'is-active' : ''}`}
                        onClick={() => setSelectedActivityId(item.id)}
                      >
                        <div className="pfp-crew-activity-list__body">
                          <strong>{item.title}</strong>
                          <span>{item.subtitle} · {item.dateLabel}</span>
                        </div>
                        <div className="pfp-crew-activity-list__meta">
                          <span className="pfp-crew-activity-list__amount">{formatCurrency(Math.abs(item.amount || 0))}</span>
                          <span className={`pfp-crew-activity-list__status ${String(item.status || '').includes('-') ? 'is-negative' : ''}`}>{item.status}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </section>

              <section className="pfp-crew-panel">
                <div className="pfp-crew-panel__header">
                  <h3>Aktivitätsdetails</h3>
                </div>
                {!selectedActivity ? (
                  <div className="pfp-crew-empty">Keine Aktivität ausgewählt.</div>
                ) : (
                  <div className="pfp-crew-activity-detail">
                    <div className="pfp-crew-activity-detail__top">
                      <div>
                        <span className="pfp-crew-section-eyebrow">Vorgang</span>
                        <h4>{selectedActivity.title}</h4>
                      </div>
                      <div className="pfp-crew-activity-detail__amount">
                        {selectedActivity.amount >= 0 ? '+' : '-'}{formatCurrency(Math.abs(selectedActivity.amount || 0))}
                      </div>
                    </div>
                    <div className="pfp-crew-activity-detail__grid">
                      <div className="pfp-crew-activity-detail__box">
                        <span>Info</span>
                        <strong>{selectedActivity.subtitle}</strong>
                      </div>
                      <div className="pfp-crew-activity-detail__box">
                        <span>Zeitpunkt</span>
                        <strong>{selectedActivity.dateLabel}</strong>
                      </div>
                    </div>
                    <div className="pfp-crew-activity-detail__note">{selectedActivity.detail}</div>
                  </div>
                )}
              </section>

              <section className="pfp-crew-panel pfp-crew-panel--full-width">
                <div className="pfp-crew-panel__header">
                  <h3>Letzte Kassenbuch-Einträge</h3>
                  <button type="button" className="pfp-crew-link-button" onClick={() => navigate(`/cashbook/${currentCrew}`)}>
                    Vollansicht öffnen
                  </button>
                </div>
                {recentCashbookEntries.length === 0 ? (
                  <div className="pfp-crew-empty">Noch keine Kassenbuch-Einträge vorhanden.</div>
                ) : (
                  <div className="pfp-crew-cashbook-preview">
                    {recentCashbookEntries.map(entry => {
                      const isPositive = Number(entry._amount) >= 0;
                      return (
                        <div key={entry.id} className="pfp-crew-cashbook-preview__row">
                          <div>
                            <div className="pfp-crew-cashbook-preview__title">{entry.reason || 'Ohne Notiz'}</div>
                            <div className="pfp-crew-cashbook-preview__meta">
                              {entry._userLabel} · {formatDateLabel(entry._parsedDate)}
                            </div>
                          </div>
                          <div className={`pfp-crew-cashbook-preview__amount ${isPositive ? 'is-positive' : 'is-negative'}`}>
                            {isPositive ? '+' : '-'}{formatCurrency(Math.abs(entry._amount))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>
            </div>
          )}

          {activeTab === 'members' && (
            <div className="pfp-crew-grid pfp-crew-grid--two-columns">
              <section className="pfp-crew-panel">
                <div className="pfp-crew-panel__header">
                  <h3>Mitglieder der Crew</h3>
                  <span>{members.length} aktiv</span>
                </div>
                <ul className="pfp-crew-member-list">
                  {members.map(member => {
                    const memberId = Number(member.ID || member.id);
                    const memberIsOwner = Number(selectedCrew?.author) === memberId;
                    return (
                      <CrewMemberRow
                        key={memberId}
                        member={{ ...member, avatarUrl: getAvatarUrl(member) }}
                        subtitle={memberIsOwner ? 'Crew-Admin' : 'Freigegebenes Mitglied'}
                        actionSlot={
                          canManageMembers && !memberIsOwner && memberId !== Number(userId) ? (
                            <ActionButton
                              variant="danger"
                              onClick={() => handleRemoveMember(memberId)}
                              disabled={actionKey === `remove-${memberId}`}
                            >
                              Entfernen
                            </ActionButton>
                          ) : null
                        }
                      />
                    );
                  })}
                </ul>
              </section>

              <section className="pfp-crew-panel">
                <div className="pfp-crew-panel__header">
                  <h3>Crew-Rangliste</h3>
                  <span>{currentPeriodLabel}</span>
                </div>
                {leaderboard.length === 0 ? (
                  <div className="pfp-crew-empty">Keine Einträge für diesen Zeitraum.</div>
                ) : (
                  <ol className="pfp-crew-ranking-list">
                    {leaderboard.map((item, index) => {
                      const member = memberMap[item.memberId] || {};
                      const avatarUrl = getAvatarUrl(member);
                      const isSelf = item.memberId === Number(userId);
                      return (
                        <li key={item.memberId} className={isSelf ? 'is-self' : ''}>
                          <span className={`pfp-crew-ranking-list__position is-${index + 1}`}>{index + 1}</span>
                          <span className="pfp-crew-ranking-list__avatar">
                            {avatarUrl ? <img src={avatarUrl} alt="" /> : <span>?</span>}
                          </span>
                          <div className="pfp-crew-ranking-list__body">
                            <span className="pfp-crew-ranking-list__name">{member.display_name || `User ${item.memberId}`}</span>
                            <span className="pfp-crew-ranking-list__sub">{item.memberId === Number(selectedCrew?.author) ? 'Crew-Admin' : 'Crew-Mitglied'}</span>
                          </div>
                          <span className="pfp-crew-ranking-list__sum">{formatCurrency(item.sum)}</span>
                        </li>
                      );
                    })}
                  </ol>
                )}
              </section>

              <section className="pfp-crew-panel">
                <div className="pfp-crew-panel__header">
                  <h3>Mitglieder-Insights</h3>
                </div>
                <div className="pfp-crew-insights-grid">
                  <div className="pfp-crew-mini-stat pfp-crew-mini-stat--wide">
                    <span>Aktivitätsquote</span>
                    <strong>{activityRate}%</strong>
                    <div className="pfp-crew-progress is-compact">
                      <span className="pfp-crew-progress__bar" style={{ width: `${activityRate}%` }} />
                    </div>
                  </div>
                  <div className="pfp-crew-mini-stat pfp-crew-mini-stat--wide">
                    <span>Ø Beitrag pro aktivem Mitglied</span>
                    <strong>{formatCurrency(averageContribution)}</strong>
                  </div>
                </div>
              </section>

              {canManageMembers && (
                <section className="pfp-crew-panel">
                  <div className="pfp-crew-panel__header">
                    <h3>Offene Anfragen</h3>
                    <span>{pendingMembers.length}</span>
                  </div>
                  {pendingMembers.length === 0 ? (
                    <div className="pfp-crew-empty">Keine offenen Beitrittsanfragen.</div>
                  ) : (
                    <ul className="pfp-crew-member-list">
                      {pendingMembers.map(member => {
                        const memberId = Number(member.ID || member.id);
                        return (
                          <CrewMemberRow
                            key={memberId}
                            member={{ ...member, avatarUrl: getAvatarUrl(member) }}
                            subtitle={`@${member.user_login || member.user_nicename || 'user'}`}
                            actionSlot={
                              <>
                                <ActionButton
                                  variant="success"
                                  onClick={() => handleApprove(memberId)}
                                  disabled={actionKey === `approve-${memberId}`}
                                >
                                  Freigeben
                                </ActionButton>
                                <ActionButton
                                  variant="danger"
                                  onClick={() => handleReject(memberId)}
                                  disabled={actionKey === `reject-${memberId}`}
                                >
                                  Ablehnen
                                </ActionButton>
                              </>
                            }
                          />
                        );
                      })}
                    </ul>
                  )}
                </section>
              )}
            </div>
          )}

          {activeTab === 'redemptions' && (
            <section className="pfp-crew-panel">
              <div className="pfp-crew-panel__header">
                <h3>Einlösungen</h3>
                <span>{currentPeriodLabel}</span>
              </div>
              {redemptionEntries.length === 0 ? (
                <div className="pfp-crew-empty">Keine Einlösungen für diesen Zeitraum.</div>
              ) : (
                <div className="pfp-crew-redemption-list">
                  {redemptionEntries.map(entry => (
                    <article key={entry.id} className="pfp-crew-redemption-list__item">
                      <div>
                        <div className="pfp-crew-redemption-list__title">{entry.reason || 'Einlösung'}</div>
                        <div className="pfp-crew-redemption-list__meta">{formatDateLabel(entry._parsedDate)} · {entry._userLabel}</div>
                      </div>
                      <div className="pfp-crew-redemption-list__amount">{formatCurrency(entry._amount)}</div>
                    </article>
                  ))}
                </div>
              )}
            </section>
          )}

          {activeTab === 'cashbook' && <CashbookView crewId={currentCrew} embedded onChanged={setCashbookEntries} />}

          {activeTab === 'map' && (
            <section className="pfp-crew-panel pfp-crew-panel--map">
              <div className="pfp-crew-panel__header">
                <h3>Live-Karte der Crew</h3>
                <span>{liveLocations.length} aktiv</span>
              </div>
              <div className="pfp-crew-sharing-controls">
                <button type="button" onClick={toggleCrewSharing}>
                  {crewSharingEnabled ? 'Standortfreigabe beenden' : 'Standort freiwillig teilen'}
                </button>
                <small>Nur Crew-Mitglieder sehen den gerundeten Standort; ohne Freigabe wird nichts gesendet.</small>
              </div>
              <Map entries={filteredEntries} colors={userColors} liveLocations={liveLocations} />
            </section>
          )}
        </>
      )}
    </div>
  );
}

export default CrewManager;
